/**
 * 작성자 : 홍선영
 * 날짜 : 2023.09.04
 * 기능 : 상황판
 */

import React, { useCallback, useEffect, useState, Component } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PulseLoader } from 'react-spinners';
import { useRecoilValue } from 'recoil';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import 'dayjs/locale/ko'; // Import Korean locale

import { IDashboard, IDashboardM } from 'customTypes';
import { IUser, userState } from '../atoms';
import { apiGet } from '../services/_common';
import MGasInfo from './dashboard/MGasInfo';
import SensorStatusWidget from './dashboard/SensorStatus';
import CctvViewer from './dashboard/CctvViewer';
import { WorkerLocation } from './dashboard/WorkerLocation';
import { WorkerAttend } from './dashboard/WorkerAttend';
import { EquipAttend } from './dashboard/EquipAttend';
import TunnelAreaInfo from './dashboard/TunnelAreaInfo';
import TunnelTotalInfo from './dashboard/TunnelTotalInfo';
import TunnelAreaInfoWithTatCd from './dashboard/TunnelAreaInfoWithTatCd';
import { TunnelExcavationProcess } from './dashboard/TunnelExcavationProcess';
import TunnelExcavationDoughnut from './dashboard/TunnelExcavationDoughnut';
import TunnelWorkAndEquipment from './dashboard/TunnelWorkAndEquipment';
import TunnelTotalInfoMini from './dashboard/TunnelTotalInfoMini';
import DateDashboard from './dashboard/Date';
import MGasInfoWithTatCd from './dashboard/MGasInfoWithTatCd';
import TunnelTotalTable from './dashboard/TunnelTotalTable';
import TunnelTotalTableWithTatCd from './dashboard/TunnelTotalTableWithTatCd';
import { WorkerAttendMultiSite } from './dashboard/WorkerAttendMultiSite';
import { WorkerAttendMultiSiteExit } from './dashboard/WorkerAttendMultiSiteExit';
import WeatherWide from './dashboard/WeatherWide';
import { WorkerAttendCountMultiSiteExit } from './dashboard/WorkerAttendCountMultiSiteExit';
import { SiteJoinAttendInfo } from './dashboard/SiteJoinAttendInfo';
import WGasInfo from './dashboard/WGasInfo';
import NetworkStatusIndicator from './NetworkStatusIndicator';
import useNetworkStatus from '../hooks/useCheckNetwork';
import GetFetchStatus from '../hooks/GetFetchStatus';
import IssueGuide from './IssueGuide';
import { WeatherCurrentInfo, WeatherInfo, WeatherWeekInfo } from './dashboard/Weather';
import { ImageBoard } from './dashboard/ImageBoard';
import { TotalInfo } from './dashboard/TotalInfo';
import { SiteNotice } from './dashboard/SiteNotice';
import { Dday } from './dashboard/Dday';
import { SafetyMainPoint } from './dashboard/SafetyMainPoint';
import { SafetyViolationInfo } from './dashboard/SafetyViolationInfo';
import { AccidentInfo } from './dashboard/AccidentInfo';
import { SafetyViolationInfoDetail } from './dashboard/SafetyViolationInfoDetail';
import { InputTable } from '../assets/styles/InputTable';
import { useTranslation } from 'react-i18next';
import { BusinessTripInfo } from './dashboard/BusinessTripInfo';
import { CompanyUserList } from './dashboard/CompanyUserList';
import UrgentNotice from './dashboard/UrgentNotice';
import TravelWeatherInfo from './dashboard/TravelWeatherInfo';
import CorrectInfo from './dashboard/CorrectInfo';
import { ControlStyle, DroppableCellStyle, SiteDashboardStyle } from '../assets/styles/SiteDashboardStyle';
import { useCalendar } from '../hooks/useCalendar';
import BigCalendar from './BigCalendar';
import { AnalogClock, ClockCalendar, DigitalClock } from './dashboard/Clock';
import { TotalInfoLarge } from './dashboard/TotalInfoLarge';
import { ImageBoardTempFading } from './dashboard/ImageBoardTempFading';
import { SiteDashboardTitle } from './dashboard/SiteDashboardTitle';
import { WeekAttendGraph } from './dashboard/WeekAttendGraph';
import { RecentAttendInfo } from './dashboard/RecentAttendInfo';
import { WeekAttendLineGraph } from './dashboard/WeekAttendLineGraph';
import { InfoCard } from './dashboard/InfoCard';

dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.locale('ko'); // Set the locale to Korean

class WidgetErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('[Widget Error]', error);
    this.retryTimer = setTimeout(() => {
      this.setState({ hasError: false });
    }, 30000);
  }

  componentWillUnmount() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.1)', borderRadius: '0.5rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#999' }}>위젯 오류 (30초 후 재시도)</span>
        </div>
      );
    }
    return children;
  }
}

export const DroppableZoneMemo = ({ cell, isViewMode, tatCd }: any) => {
  return (
    <DroppableCellStyle
      style={{
        gridColumnStart: cell.location.column + 1,
        gridRowStart: cell.location.row + 1,
        gridColumnEnd: cell.location.column + cell.width + 1,
        gridRowEnd: cell.location.row + cell.height + 1,
        backgroundColor: cell.name === '' ? '#fff' : undefined,
      }}
      isViewMode={isViewMode}
    >
      <span className='dropCell' style={cell.name === '' ? { display: 'none' } : { display: 'block' }}>
        {/* <WidgetErrorBoundary> */}
        <Control content={cell} tatCd={tatCd} />
        {/* </WidgetErrorBoundary> */}
      </span>
    </DroppableCellStyle>
  );
};

export const DroppableZone = React.memo(DroppableZoneMemo); // 하위 컴포넌트에서 불필요한 리랜더링 방지

interface IProps {
  isViewMode: boolean; // 세팅화면인지/뷰화면인지 여부
  dGubun: string; // 현장용 일반 상황판인지, 구역별 상황판인지 타입
  tatCd?: string; // 구역코드
}
const ONE_HOUR = 60 * 60 * 1000; // 60분
const HALF_HOUR = 60 * 30 * 1000; // 30분
const FIVE_MINUTES = 5 * 60 * 1000; // 5분

const SiteDashboard = ({ isViewMode, dGubun, tatCd }: IProps) => {
  const userInfo = useRecoilValue(userState);
  const dGubunCd = dGubun && dGubun;
  const [dropZoneGrid, setDropZoneGrid] = useState<any[] | null>(null);
  const isOnline = useNetworkStatus();
  const [memory, setMemory] = useState<{ used: string; total: string; limit: string }>({ used: '0', total: '0', limit: '0' });

  const {
    data: dashboardMListData,
    isLoading: isLoadingDashboardMListData,
    isError: isErrorDashboardMList,
  } = GetFetchStatus({ path: '/dashboard/master', req: { dGubun: dGubunCd }, queryKey: `${dGubunCd}` });
  const {
    data: dashboardSetListData,
    isLoading: isLoadingDashboardSetList,
    isError: isErrorDashboardSetList,
  } = GetFetchStatus({ path: '/dashboard', req: { dGubun: dGubunCd }, queryKey: `${dGubunCd}` });

  useEffect(() => {
    if (dashboardSetListData && dashboardMListData) {
      const { DashboardSetList } = dashboardSetListData;
      const { DashboardMList } = dashboardMListData;
      const useYDashboard = DashboardSetList.filter((setListEl: IDashboard) => setListEl.useYn === 'Y');
      const result = useYDashboard.map((setListEl: IDashboard) => {
        const dataItem = DashboardMList.find((mListEl: IDashboardM) => mListEl.dCd === setListEl.dCd);
        return {
          dCd: setListEl.dCd,
          name: setListEl.dName,
          isInDropZone: !!dataItem,
          ...(dataItem ? { location: { column: dataItem.dCol, row: dataItem.dRow } } : { location: { column: -1, row: -1 } }),
          width: setListEl.dCol,
          height: setListEl.dRow,
        };
      });

      const mergedGrid = result.map((gridItem: any) => {
        const matchPiece = result.find((piece: any) => piece.dCd === gridItem.dCd);
        return matchPiece ? { ...gridItem, ...matchPiece } : gridItem;
      });
      setDropZoneGrid(mergedGrid);
    }
  }, [dashboardMListData, dashboardSetListData]);

  const isError = isErrorDashboardMList || isErrorDashboardSetList;
  const isLoading = isLoadingDashboardMListData || isLoadingDashboardSetList || !dashboardMListData || !dashboardSetListData || !dropZoneGrid;

  if (isError) return <IssueGuide />;
  if (isLoading) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }

  return (
    <>
      <NetworkStatusIndicator />
      {/** 네트워크 끊긴 경우
       * C001일 때에만 백그라운드에 상황판 노출
       * 나머지 현장은 네트워크 연결오류 모달만 노출 */}
      {(isOnline || userInfo.hCd === 'C001') && (
        <SiteDashboardStyle isViewMode={isViewMode}>
          <div className='dragNDropZone'>
            <div className='dropZone'>{dropZoneGrid?.map((el) => el.isInDropZone && <DroppableZone key={el.dCd} cell={el} isViewMode={isViewMode} tatCd={tatCd} />)}</div>
          </div>
        </SiteDashboardStyle>
      )}
    </>
  );
};

export default SiteDashboard;

interface IControl {
  content: any;
  tatCd?: string;
}

const Control = ({ content, tatCd }: IControl) => {
  const { dCd = '', name = '', isInDropZone = false } = content && content;
  const userInfo = useRecoilValue(userState);
  const [currentDate, setCurrentDate] = useState(new Date());
  const auth = {
    createAuth: false,
    updateAuth: false,
    deleteAuth: false,
    printAuth: false,
    excelAuth: false,
  };

  const getDiffRefetchTime = useCallback(
    (dCdParam: string, sec: number) => {
      const time = sec + Number(dCdParam) * 2 * 1000;
      if (isNaN(time)) return 1800000; // 30분
      return time;
    },
    [dCd]
  );

  const {
    scheduleList,
    isLoading: isLoadingSchedules,
    refetchSchedule,
    queryClient,
    headInfoList,
    scheduleInfoList,
    companyUser,
    userPartList,
    carInfoList,
    isLoading,
    isRefetchingSchedules,
  } = useCalendar({ enabled: dCd === '098' || dCd === '099', currentDate, setCurrentDate, auth, refetchInterval: getDiffRefetchTime(dCd, HALF_HOUR) });

  return (
    <ControlStyle>
      {/* 기상정보 */}
      {dCd === '031' && isInDropZone && <WeatherInfo name={name} refetchInterval={getDiffRefetchTime(dCd, ONE_HOUR)} />}
      {/* 기상정보 */}
      {(dCd === '074' || dCd === '075') && isInDropZone && <WeatherWide userInfo={userInfo} width={dCd === '074' ? 'large' : 'medium'} refetchInterval={getDiffRefetchTime(dCd, ONE_HOUR)} />}
      {/* 기상 정보 (주간) */}
      {dCd === '032' && isInDropZone && <WeatherWeekInfo refetchInterval={getDiffRefetchTime(dCd, ONE_HOUR)} />}
      {/* 종합 현황 */}
      {dCd === '003' && isInDropZone && <TotalInfo name={name} userInfo={userInfo} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, FIVE_MINUTES)} />}
      {/* 종합 현황 */}
      {dCd === '151' && isInDropZone && <TotalInfo name={name} userInfo={userInfo} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, FIVE_MINUTES)} />}
      {/* 종합 현황 대형 */}
      {(dCd === '117' || dCd === '125') && isInDropZone && <TotalInfoLarge name={name} userInfo={userInfo} refetchInterval={getDiffRefetchTime(dCd, FIVE_MINUTES)} />}
      {/* D-DAY */}
      {dCd === '021' && isInDropZone && <InfoCard name={name} userInfo={userInfo} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, FIVE_MINUTES)} />}
      {/* 현장 전달사항 (소형) */}
      {dCd === '022' && isInDropZone && <SiteNotice name={name} userInfo={userInfo} size='small' refetchInterval={getDiffRefetchTime(dCd, FIVE_MINUTES)} />}
      {/* 현장 전달사항 (대형) */}
      {dCd === '111' && isInDropZone && <SiteNotice name={name} userInfo={userInfo} size='large' refetchInterval={getDiffRefetchTime(dCd, FIVE_MINUTES)} />}
      {/* 안전 중점사항 */}
      {(dCd === '023' || dCd === '076' || dCd === '096' || dCd === '097') && isInDropZone && (
        <SafetyMainPoint userInfo={userInfo} type={dCd === '096' || dCd === '097' ? 'c' : 's'} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />
      )}
      {/* 이미지 게시판 */}
      {dCd === '024' && isInDropZone && <ImageBoard name={name} userInfo={userInfo} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 안전 위반 현황 */}
      {dCd === '026' && isInDropZone && <SafetyViolationInfo name={name} userInfo={userInfo} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 무사고 현황 */}
      {dCd === '028' && isInDropZone && <InfoCard name={name} userInfo={userInfo} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 안전 위반 현황 상세 */}
      {dCd === '030' && isInDropZone && <SafetyViolationInfoDetail name={name} userInfo={userInfo} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 유해가스 종합현황 (도형) */}
      {dCd === '036' && isInDropZone && <MGasInfo size='large' valueType='shape' refetchInterval={getDiffRefetchTime(dCd, 60000)} />}
      {/* 굴진 진행 현황 */}
      {dCd === '038' && isInDropZone && <ExcavationProcess name={name} userInfo={userInfo} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 근로자 위치 현황 */}
      {dCd === '040' && isInDropZone && <ExcavationWorkerPosition name={name} userInfo={userInfo} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 유해가스 종합현황 (숫자) */}
      {dCd === '045' && isInDropZone && <MGasInfo size='large' valueType='number' refetchInterval={getDiffRefetchTime(dCd, 60000)} />}
      {/* 터널 종합 진행 현황(대형) */}
      {dCd === '052' && isInDropZone && <TunnelTotalInfo size='large' visibleRefreshBtn={false} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 터널 종합 진행 현황 */}
      {dCd === '053' && isInDropZone && <TunnelTotalInfo visibleRefreshBtn={false} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 터널 구역별 종합 진행 현황(대형) */}
      {dCd === '054' && isInDropZone && <TunnelAreaInfo size='large' visibleRefreshBtn={false} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 터널 구역별 종합 진행 현황 */}
      {dCd === '055' && isInDropZone && <TunnelAreaInfo size='large' visibleRefreshBtn={false} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 터널 종합 진행 현황(일반) */}
      {dCd === '056' && isInDropZone && <TunnelTotalTable uniqueKey={dCd} visibleRefreshBtn={false} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 근로자 출입현황(다중현장용) */}
      {dCd === '070' && isInDropZone && <WorkerAttendMultiSite userInfo={userInfo} refetchInterval={getDiffRefetchTime(dCd, 60000)} uniqueKey={dCd} />}
      {/* 근로자 출입현황(다중현장용(1개 출입구용)) */}
      {dCd === '071' && isInDropZone && <WorkerAttendMultiSiteExit userInfo={userInfo} refetchInterval={getDiffRefetchTime(dCd, 60000)} uniqueKey={dCd} />}
      {/* 근로자 출입현황(다중현장용(1개 출입구용, 인원만)) */}
      {(dCd === '072' || dCd === '073') && isInDropZone && (
        <WorkerAttendCountMultiSiteExit name='근로자 출입현황' userInfo={userInfo} refetchInterval={60000} uniqueKey={dCd} slidesToShow={dCd === '072' ? 5 : 2} size='large' />
      )}
      {/* 근로자 출입현황 소형 (다중현장용(1개 출입구용, 인원만)) */}
      {dCd === '116' && isInDropZone && <WorkerAttendCountMultiSiteExit name='근로자 출입현황' userInfo={userInfo} refetchInterval={60000} uniqueKey={dCd} slidesToShow={1} size='small' />}
      {/* 협력업체 출역 정보 */}
      {dCd === '077' && isInDropZone && <SiteJoinAttendInfo name={name} userInfo={userInfo} refetchInterval={getDiffRefetchTime(dCd, 300000)} />}
      {/* 근로자 출입현황(다중현장용(1개 출입구용, 인원만, 롯데건설-인천효성3블럭 용으로만 사용)) */}
      {(dCd === '078' || dCd === '082') && isInDropZone && (
        <WorkerAttendCountMultiSiteExit name='근로자 출입현황' userInfo={{ hCd: 'H002', sCd: 'S0001' }} refetchInterval={60000} uniqueKey={dCd} slidesToShow={dCd === '078' ? 5 : 2} size='large' />
      )}

      {/* 무선 유해가스 종합현황 (도형) */}
      {dCd === '087' && isInDropZone && <WGasInfo autoRefresh autoPlay size='large' valueType='shape' uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, 60000)} />}
      {/* 무선 유해가스 종합현황 (숫자) */}
      {dCd === '088' && isInDropZone && <WGasInfo autoRefresh size='large' autoPlay valueType='number' uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, 60000)} />}
      {/* 근로자 출입현황(다중현장용(1개 출입구용, 인원만, 롯데건설-인천효성3블럭 용으로만 사용, 슬라이딩제거)) */}
      {dCd === '090' && isInDropZone && (
        <WorkerAttendCountMultiSiteExit name='근로자 출입현황' userInfo={{ hCd: 'H002', sCd: 'S0001' }} refetchInterval={60000} uniqueKey={dCd} slidesToShow={3} size='large' />
      )}
      {/** 출장 현황 */}
      {dCd === '091' && isInDropZone && <BusinessTripInfo name={name} autoRefresh />}
      {/** 우수사원/휴가자/생일자 */}
      {dCd === '092' && isInDropZone && <CompanyUserList autoRefresh />}
      {/* 긴급공지 */}
      {dCd === '093' && isInDropZone && <UrgentNotice name={name} userInfo={userInfo} refreshTimeCount={1800} />}
      {/* 출장 현장 기상 정보 */}
      {dCd === '094' && isInDropZone && <TravelWeatherInfo name={name} userInfo={userInfo} refreshTimeCount={1800} />}
      {/* 시정사항 & 조치내용 정보 */}
      {dCd === '095' && isInDropZone && <CorrectInfo name={name} userInfo={userInfo} refreshTimeCount={1800} />}
      {/* 일정 관리(캘린더) */}
      {dCd === '099' && isInDropZone && !isLoadingSchedules && !isLoading && scheduleList && (
        <BigCalendar
          events={scheduleList}
          refetch={refetchSchedule}
          queryClient={queryClient}
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          auth={auth}
          isSearchSmGubunAll
          headInfoList={headInfoList}
          scheduleInfoList={scheduleInfoList}
          userPartList={userPartList}
          carInfoList={carInfoList}
          companyUser={companyUser}
          hideOptionalToolBar
          isLoading={isRefetchingSchedules}
        />
      )}
      {/* 아날로그 시계 ( 작은 위젯 - 가로 2, 세로 5 ) */}
      {dCd === '105' && isInDropZone && <AnalogClock viewType='s' size='xs' />}
      {/* 디지털 시계 ( 작은 위젯 - 가로 1, 세로 5 ) */}
      {dCd === '106' && isInDropZone && <DigitalClock viewType='s' size='xs' />}
      {/* 시계 및 캘린더 ( 큰 위젯 - 가로 3, 세로 5 ) */}
      {dCd === '107' && isInDropZone && <ClockCalendar viewType='s' size='xs' />}
      {/* 아날로그 시계 ( 큰 위젯 - 가로 2, 세로 5 ) */}
      {dCd === '108' && isInDropZone && <AnalogClock viewType='s' />}
      {/* 디지털 시계 ( 큰 위젯 - 가로 2, 세로 5 ) */}
      {dCd === '109' && isInDropZone && <DigitalClock viewType='s' />}
      {/* 시계 및 캘린더 ( 큰 위젯 - 가로 3, 세로 5 ) */}
      {dCd === '110' && isInDropZone && <ClockCalendar viewType='s' />}
      {/* 상황판 타이틀 */}
      {dCd === '123' && isInDropZone && <SiteDashboardTitle userInfo={userInfo} type={userInfo.hCd.substring(0, 1).toLowerCase() === 'c' ? 'c' : 's'} isPM={false} />}
      {/* 상황판 타이틀 ( 날씨 - 미세먼지 포함용 ) */}
      {dCd === '147' && isInDropZone && <SiteDashboardTitle userInfo={userInfo} type={userInfo.hCd.substring(0, 1).toLowerCase() === 'c' ? 'c' : 's'} isPM={!false} />}
      {/* 주간 출역 그래프(막대) */}
      {dCd === '124' && isInDropZone && <WeekAttendGraph userInfo={userInfo} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, FIVE_MINUTES)} />}
      {/** 실시간 출역 현황 ( 156: 세로 스택(상단 카드) 버전) */}
      {(dCd === '126' || dCd === '127' || dCd === '156') && isInDropZone && <RecentAttendInfo userInfo={userInfo} uniqueKey={dCd} refetchInterval={60000} />}
      {/* 주간 출역 그래프(꺾은선) */}
      {dCd === '128' && isInDropZone && <WeekAttendLineGraph userInfo={userInfo} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, FIVE_MINUTES)} />}
      {/* 출입금지 */}
      {dCd === '129' && isInDropZone && <InfoCard name={name} userInfo={userInfo} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, FIVE_MINUTES)} />}
      {/* 안전지적 */}
      {dCd === '130' && isInDropZone && <InfoCard name={name} userInfo={userInfo} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, FIVE_MINUTES)} />}
      {/* 금일 출역현황 */}
      {dCd === '131' && isInDropZone && <InfoCard name={name} userInfo={userInfo} uniqueKey={dCd} refetchInterval={60000} />}
      {/* 금일 출역현황 */}
      {dCd === '131' && isInDropZone && <InfoCard name={name} userInfo={userInfo} uniqueKey={dCd} refetchInterval={60000} />}
      {/* 금일 신규근로자 */}
      {dCd === '148' && isInDropZone && <InfoCard name={name} userInfo={userInfo} uniqueKey={dCd} refetchInterval={60000} />}
      {/* 금일 퇴근인원 */}
      {dCd === '149' && isInDropZone && <InfoCard name={name} userInfo={userInfo} uniqueKey={dCd} refetchInterval={60000} />}
      {/* 현재 잔류인원 */}
      {dCd === '150' && isInDropZone && <InfoCard name={name} userInfo={userInfo} uniqueKey={dCd} refetchInterval={60000} />}
      {/* 위치별 센서 종합현황 */}
      {dCd === '153' && isInDropZone && <SensorStatusWidget name={name} refetchInterval={60000} />}
      {/* 현장 CCTV (1분할) - 상황판 상시 노출용 자동재생 */}
      {dCd === '154' && isInDropZone && <CctvViewer name={name} divisions={1} viewOnly />}
      {/* 현장 CCTV (4분할) - 상황판 상시 노출용 자동재생 */}
      {dCd === '155' && isInDropZone && <CctvViewer name={name} divisions={4} viewOnly />}

      {/*  */}
      {/* 구역별 상황판 */}
      {/* 터널 구역별 종합 진행 현황(대형) */}
      {dCd === '057' && isInDropZone && tatCd && <TunnelAreaInfoWithTatCd tatCd={tatCd} uniqueKey={dCd} visibleRefreshBtn={false} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 터널 구역별 종합 진행 현황 */}
      {dCd === '058' && isInDropZone && tatCd && <TunnelAreaInfoWithTatCd tatCd={tatCd} uniqueKey={dCd} visibleRefreshBtn={false} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {dCd === '059' && isInDropZone && tatCd && <TunnelTotalTableWithTatCd userInfo={userInfo} tatCd={tatCd} uniqueKey={dCd} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 현재일자 / 시간 */}
      {dCd === '060' && isInDropZone && <DateDashboard />}
      {/* 유해가스 종합현황 (원형) */}
      {dCd === '061' && isInDropZone && tatCd && <MGasInfoWithTatCd name={name} userInfo={userInfo} tatCd={tatCd} refetchInterval={getDiffRefetchTime(dCd, 60000)} />}
      {/* 굴진 진행 현황 */}
      {dCd === '062' && isInDropZone && tatCd && <TunnelExcavationProcess name={name} userInfo={userInfo} tatCd={tatCd} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 터널 작업 / 장비 현황 */}
      {dCd === '063' && isInDropZone && tatCd && <TunnelWorkAndEquipment userInfo={userInfo} tatCd={tatCd} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 터널 종합 진행 현황(소형) */}
      {dCd === '064' && isInDropZone && tatCd && <TunnelTotalInfoMini userInfo={userInfo} tatCd={tatCd} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 근로자 위치 현황 */}
      {dCd === '065' && isInDropZone && tatCd && <WorkerLocation name={name} userInfo={userInfo} tatCd={tatCd} refetchInterval={getDiffRefetchTime(dCd, 60000)} />}
      {/* 근로자 출입 현황 */}
      {dCd === '066' && isInDropZone && tatCd && <WorkerAttend name={name} userInfo={userInfo} refetchInterval={getDiffRefetchTime(dCd, 60000)} tatCd={tatCd} />}
      {/* 장비 출입 현황 */}
      {dCd === '067' && isInDropZone && tatCd && <EquipAttend name={name} userInfo={userInfo} refetchInterval={getDiffRefetchTime(dCd, 60000)} tatCd={tatCd} />}
      {/* 굴진 진행 현황(도넛형) */}
      {dCd === '068' && isInDropZone && tatCd && <TunnelExcavationDoughnut name={name} userInfo={userInfo} tatCd={tatCd} refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} />}
      {/* 유해가스 종합현황 (수치) */}
      {dCd === '069' && isInDropZone && tatCd && <MGasInfoWithTatCd name={name} userInfo={userInfo} valueType='number' tatCd={tatCd} refetchInterval={getDiffRefetchTime(dCd, 60000)} />}
      {/* 디지털 시계 ( 큰 위젯 - 가로 1, 세로 3 ) */}
      {dCd === '112' && isInDropZone && <AnalogClock viewType='s' size='sm' />}
      {/* 시계 및 캘린더 ( 큰 위젯 - 가로 1, 세로 3 ) */}
      {dCd === '113' && isInDropZone && <DigitalClock viewType='s' size='sm' />}
      {/* 현재날씨 */}
      {dCd === '114' && isInDropZone && <WeatherCurrentInfo name={name} size='small' refetchInterval={getDiffRefetchTime(dCd, ONE_HOUR)} />}
      {/* 이미지 게시판 사내용 임시 (중형) */}
      {dCd === '115' && isInDropZone && <ImageBoardTempFading refetchInterval={getDiffRefetchTime(dCd, HALF_HOUR)} uniqueKey={dCd} />}
    </ControlStyle>
  );
};

const ExcavationProcess = ({ name, userInfo, refetchInterval }: { name: string; userInfo: IUser; refetchInterval: number }) => {
  const { t } = useTranslation();
  const equipQuery = useQuery(['equipInfoGet', userInfo], () => apiGet({ path: '/setting/faceSet', req: { hCd: userInfo.hCd, sCd: userInfo.sCd } }), {
    refetchInterval,
  });

  const dummyData = [
    { name: '시작부_1', point: 0, value: 65 },
    { name: '시작부_2', point: 0, value: 46 },
    { name: '종점부_1', point: 1, value: 73 },
    { name: '종점부_2', point: 1, value: 36 },
    { name: '종점부_3', point: 1, value: 25 },
    { name: '종점부_4', point: 1, value: 12 },
  ];

  if (!equipQuery.isSuccess || equipQuery.data.status !== 200 || equipQuery.isRefetching) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }
  return (
    <div className='equipInfo excavationProcess'>
      <div className='widget-header margin-top'>
        <div className='widget-title flex-between'>
          <span>{t(name)}</span>
        </div>
      </div>
      <div className='widget-body table' style={{ width: '100%' }}>
        <InputTable>
          <div className='thead'>
            <div className='tr'>
              <div className='flex-center col-w40'>{t('굴진명')}</div>
              <div className='flex-center col-w60'>{t('진행률')}</div>
            </div>
          </div>
          <div className='table' style={{ overflow: 'hidden' }}>
            <div className='tbody' style={{ animation: `slide ${dummyData.length * 2}s linear infinite` }}>
              {dummyData?.length > 0 ? (
                dummyData.map((el: any, i: number) => (
                  <div className='tr' key={`equipInfo_${i}`}>
                    <div className='flex-center col-w40'>
                      {el.name} <span className={`textBox ${el.point === 0 ? 'blue' : 'slate'}`}>{el.point === 0 ? t('시점') : t('종점')}</span>
                    </div>
                    <div className='col-w60 progress-container'>
                      <progress id={`equipInfo_${i}`} value={el.value} max='100' />
                      <div className='progressValue' style={{ transform: `${el.value >= 12 ? `translateX(${(el.value - 8) / 2}%)` : ''}` }}>
                        {el.value}%
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className='noData'>No data.</div>
              )}
            </div>
          </div>
        </InputTable>
      </div>
    </div>
  );
};

const ExcavationWorkerPosition = ({ name, userInfo, refetchInterval }: { name: string; userInfo: IUser; refetchInterval: number }) => {
  const { t } = useTranslation();
  const equipQuery = useQuery(['equipInfoGet', userInfo], () => apiGet({ path: '/setting/faceSet', req: { hCd: userInfo.hCd, sCd: userInfo.sCd } }), { refetchInterval });

  const dummyData = [
    { name: '시작부_1', value: 5, point: 0 },
    { name: '시작부_2', value: 16, point: 0 },
    { name: '종점부_1', value: 23, point: 1 },
    { name: '종점부_2', value: 18, point: 1 },
    { name: '종점부_3', value: 25, point: 1 },
    { name: '종점부_4', value: 1, point: 1 },
  ];

  if (!equipQuery.isSuccess || equipQuery.data.status !== 200 || equipQuery.isRefetching) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }
  return (
    <div className='equipInfo excavationProcess'>
      <div className='widget-header margin-top'>
        <div className='widget-title flex-between'>
          <span>{t(name)}</span>
        </div>
      </div>
      <div className='widget-body table' style={{ width: '100%' }}>
        <InputTable>
          <div className='thead'>
            <div className='tr'>
              <div className='flex-center col-w40'>{t('굴진명')}</div>
              <div className='flex-center col-w60'>{t('근로자 인원')}</div>
            </div>
          </div>
          <div className='table' style={{ overflow: 'hidden' }}>
            <div className='tbody' style={{ animation: `slide ${dummyData.length * 2}s linear infinite` }}>
              {dummyData?.length > 0 ? (
                dummyData.map((el: any, i: number) => (
                  <div className='tr' key={`equipInfo_${i}`}>
                    <div className='flex-center col-w40'>
                      {el.name}
                      <span className={`textBox ${el.point === 0 ? 'blue' : 'slate'}`}>{el.point === 0 ? t('시점') : t('종점')}</span>
                    </div>
                    <div className='flex-center col-w60'>{el.value}</div>
                  </div>
                ))
              ) : (
                <div className='noData'>No data.</div>
              )}
            </div>
          </div>
        </InputTable>
      </div>
    </div>
  );
};
